import { useState, useRef, useEffect, type CSSProperties, type KeyboardEvent } from 'react';
import { useApi } from '../hooks/useApi';
import { useServerPage } from '../hooks/useServerPage';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Confirm } from '../components/Confirm';
import { MenuImportar } from '../components/MenuImportar';
import { BuscadorSku, MIN_CARACTERES_SKU, type SkuSugerido } from '../components/BuscadorSku';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';
import { ChipsVariante } from '../components/ChipsVariante';
import { ahoraLocalAr } from '../lib/fechaHoraAr';
import { useAvisoImpactoMl } from '../hooks/useAvisoImpactoMl';

interface Cliente { id: number; nombre: string; }
interface Articulo extends SkuSugerido { mlaId: string | null; precioVenta: number | null; }
/**
 * Renglón del formulario. `resuelto` = el SKU que ya se completó desde el catálogo (no se vuelve a buscar al salir).
 * `fueraDeCatalogo`: lo escrito en el buscador no está en el catálogo; 'mla' = se pasó a la columna MLA (venta por
 * publicación, sin reservar stock), 'sku' = no se puede guardar hasta elegir un artículo.
 */
interface Linea {
  _key: string; skuInterno: string; resuelto?: string; mlaId: string; descripcion: string;
  cantidad: number; precioUnitario: number; descuentoPorcentaje: number; varianteId?: number | null;
  fueraDeCatalogo?: 'mla' | 'sku';
}
const PARECE_MLA = /^MLA\d+$/i;
interface OrdenVentaItemDetalle {
  mlaId: string; descripcion: string; cantidad: number; precioUnitario: number; descuentoPorcentaje?: number; subtotal?: number;
  skuInterno?: string | null; varianteId?: number | null; variante?: { nombre: string } | null;
}
/** Colores en los que se vende un SKU (backend: variantesParaVenta); `porBom` = manda el BOM y no se elige. */
interface ColoresVenta { porBom: boolean; variosFisicos?: boolean; variantes: Array<{ id: number; nombre: string; colorHex: string | null; cantidad: number }>; }
interface OrdenVenta {
  id: number;
  clienteId?: number | null;
  clienteNombre?: string;
  fecha: string;
  numeroOrden?: string;
  estado: string;
  canal: string;
  modalidadEnvio?: string;
  total: number;
  notas?: string | null;
  direccionEntrega?: string | null;
  items?: OrdenVentaItemDetalle[];
}

const ML = 'mercadolibre';
const CANAL: Record<string, string> = { mostrador: 'Mostrador', [ML]: 'MercadoLibre' };
const ESTADO_OV: Record<string, { etiqueta: string; tono: 'slate' | 'blue' | 'brand' | 'green' | 'red' }> = {
  borrador: { etiqueta: 'Borrador', tono: 'slate' },
  confirmada: { etiqueta: 'Confirmada', tono: 'blue' },
  entregada: { etiqueta: 'Entregada', tono: 'brand' },
  facturada: { etiqueta: 'Facturada', tono: 'green' },
  cancelada: { etiqueta: 'Cancelada', tono: 'red' },
};
const MODALIDAD_TONO: Record<string, 'blue' | 'green' | 'orange' | 'slate'> = { Full: 'blue', Flex: 'green', Colecta: 'orange' };
const SIN_PERMISO = 'No tenés permiso para realizar esta acción';

const today = () => ahoraLocalAr().slice(0, 10);
let ultimaClave = 0;
const lineaVacia = (): Linea => ({ _key: `l${++ultimaClave}`, skuInterno: '', mlaId: '', descripcion: '', cantidad: 1, precioUnitario: 0, descuentoPorcentaje: 0 });
const emptyForm = () => ({ clienteId: '', clienteTexto: '', canal: 'mostrador', fecha: today(), numeroOrden: '', direccionEntrega: '', notas: '', items: [lineaVacia()] });
const formDe = (ov: OrdenVenta) => ({
  clienteId: ov.clienteId ? String(ov.clienteId) : '', clienteTexto: ov.clienteNombre ?? '', canal: ov.canal, fecha: ov.fecha.slice(0, 10),
  numeroOrden: ov.numeroOrden ?? '', direccionEntrega: ov.direccionEntrega ?? '', notas: ov.notas ?? '',
  items: (ov.items ?? []).map((it): Linea => ({
    ...lineaVacia(), skuInterno: it.skuInterno ?? '', resuelto: it.skuInterno ?? undefined, mlaId: it.mlaId ?? '', descripcion: it.descripcion,
    cantidad: Number(it.cantidad), precioUnitario: Number(it.precioUnitario), descuentoPorcentaje: Number(it.descuentoPorcentaje ?? 0), varianteId: it.varianteId ?? null,
  })),
});
const claseFiltro = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm';
const CAMPO = 'w-full px-2 py-1.5 border rounded-lg text-sm';
// Etiqueta de cada campo del renglón en el celular (en escritorio está el encabezado de columnas).
const ETIQUETA = 'md:hidden block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1';
// Columnas de los renglones en escritorio (misma técnica que Compras: la plantilla en una variable CSS del contenedor).
const COLUMNAS_LINEA = 'md:grid-cols-[var(--ov-columnas)]';
const ESTILO_COLUMNAS = {
  '--ov-columnas': 'minmax(9rem,1.4fr) minmax(7rem,1fr) minmax(10rem,3fr) minmax(4rem,0.6fr) minmax(5.5rem,0.9fr) minmax(4rem,0.6fr) minmax(6rem,1fr) 2rem',
} as CSSProperties;

const pesos = (n: number) => `$ ${n.toLocaleString('es-AR', { maximumFractionDigits: 2 })}`;
const brutoLinea = (it: Pick<Linea, 'cantidad' | 'precioUnitario'>) => (Number(it.cantidad) || 0) * (Number(it.precioUnitario) || 0);
const subtotalLinea = (it: Linea) => brutoLinea(it) * (1 - (Number(it.descuentoPorcentaje) || 0) / 100);
/** Busca en el catálogo por SKU, descripción o MLA (estable: va al BuscadorSku). */
const buscarArticulos = async (q: string): Promise<Articulo[]> => {
  const r = await api.get<Articulo[]>(`/api/v3/ventas/articulos?q=${encodeURIComponent(q)}&limit=12`);
  return Array.isArray(r) ? r : [];
};

export function VentasPage() {
  // Filtros y paginación en el backend (listarOrdenes): fecha es @db.Date, así que fecha_hasta ya incluye ese día.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [filtros, setFiltros] = useState({ canal: '', estado: '', fecha_desde: '', fecha_hasta: '' });
  const filtrar = (k: keyof typeof filtros, v: string) => { setFiltros(f => ({ ...f, [k]: v })); setPage(1); };
  const { data, loading, total: totalOrdenes, refetch } = useServerPage<OrdenVenta>('/api/v3/ventas', page, pageSize, search, 'q', filtros, { serverSideFilters: true });
  const { data: clientes } = useApi<Cliente[]>('/api/v3/clientes');
  const [open, setOpen] = useState(false);
  // OV en borrador que se está editando (null = nueva).
  const [editando, setEditando] = useState<OrdenVenta | null>(null);
  const [detalle, setDetalle] = useState<OrdenVenta | null>(null);
  const [aCancelar, setACancelar] = useState<OrdenVenta | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [verClientes, setVerClientes] = useState(false);
  const [guardando, setGuardando] = useState<false | 'borrador' | 'confirmar'>(false);
  const [mlSyncOpen, setMlSyncOpen] = useState(false);
  const [mlOrderId, setMlOrderId] = useState('');
  const [mlSyncing, setMlSyncing] = useState(false);
  const [itemsError, setItemsError] = useState('');
  const [foco, setFoco] = useState<{ key: string; campo: 'buscar' | 'cant' } | null>(null);
  const cerrarForm = () => { setOpen(false); setEditando(null); setForm(emptyForm()); setItemsError(''); };
  const cierreForm = useCierreConfirmado(useHayCambios(form, open), cerrarForm);
  // Obligatorios de la OV (backend: CreateOrdenVentaDto, OrdenVentaItemDto y normalizarItems): la fecha y, en cada
  // ítem, el SKU o el MLA ID (en canal MercadoLibre, el MLA ID), la descripción, la cantidad (mayor a 0) y el precio.
  // El cliente es opcional (mostrador).
  const obligatorios = useCamposObligatorios(open);
  // Importar de ML: el ID de la orden (va en la URL)
  const obligatoriosMl = useCamposObligatorios(mlSyncOpen);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    const t = setInterval(() => refetch(), 30000);
    return () => clearInterval(t);
  }, [refetch]);
  const [coloresPorSku, setColoresPorSku] = useState<Record<string, ColoresVenta>>({});
  const toast = useToast();
  const avisarImpactoMl = useAvisoImpactoMl();
  const { can } = useAuth();

  const esMl = form.canal === ML;
  const bruto = form.items.reduce((s, it) => s + brutoLinea(it), 0);
  const total = form.items.reduce((s, it) => s + subtotalLinea(it), 0);
  const clientesFiltrados = (clientes ?? []).filter(c => c.nombre.toLowerCase().includes(form.clienteTexto.trim().toLowerCase())).slice(0, 12);

  const updateItem = (key: string, patch: Partial<Linea>) =>
    setForm(f => ({ ...f, items: f.items.map(it => (it._key === key ? { ...it, ...patch } : it)) }));

  // Enfoca un campo de un renglón después de dibujarlo (el buscador de uno nuevo, la cantidad al elegir el artículo).
  useEffect(() => {
    if (!foco) return;
    document.querySelector<HTMLInputElement>(`[data-fila="${foco.key}"] [data-${foco.campo}]`)?.focus();
    setFoco(null);
  }, [foco]);
  const agregarLinea = () => { const l = lineaVacia(); setForm(f => ({ ...f, items: [...f.items, l] })); setFoco({ key: l._key, campo: 'buscar' }); };
  // Enter en cantidad, precio o descuento: al buscador del renglón siguiente (en el último, agrega uno).
  const alSiguienteRenglon = (idx: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const siguiente = form.items[idx + 1];
    if (siguiente) setFoco({ key: siguiente._key, campo: 'buscar' }); else agregarLinea();
  };
  const quitarLinea = (key: string) => setForm(f => {
    const items = f.items.filter(it => it._key !== key);
    return { ...f, items: items.length ? items : [lineaVacia()] };
  });

  /** Trae los colores de un SKU para el selector; si falla lo avisa (el backend igual vuelve a validar al crear). */
  const cargarColores = (sku: string) => {
    if (coloresPorSku[sku]) return;
    api.get<ColoresVenta>(`/api/v3/ventas/articulos/${encodeURIComponent(sku)}/variantes`)
      .then(c => setColoresPorSku(m => ({ ...m, [sku]: c })))
      .catch(e => toast.addToast(`No se pudieron traer los colores de ${sku}: ${extractErrorMessage(e)}`, 'error'));
  };

  // Al elegir el artículo se completan SKU, descripción, precio y (si está vacío) el MLA; el color se vuelve a elegir.
  const aplicarArticulo = (key: string, a: Articulo) => {
    setForm(f => ({
      ...f,
      items: f.items.map(it => it._key === key ? {
        ...it,
        skuInterno: a.skuInterno,
        resuelto: a.skuInterno,
        fueraDeCatalogo: undefined,
        mlaId: it.mlaId || a.mlaId || '',
        varianteId: null,
        descripcion: a.descripcion ?? it.descripcion,
        precioUnitario: a.precioVenta != null ? Number(a.precioVenta) : it.precioUnitario,
      } : it),
    }));
    cargarColores(a.skuInterno);
  };
  const elegirArticulo = (key: string, a: Articulo) => { aplicarArticulo(key, a); setFoco({ key, campo: 'cant' }); };

  // Si se escribió el SKU (o el MLA) sin elegir de la lista, al salir del campo se busca el artículo para completar el
  // renglón y poder pedir el color. Sin coincidencia: un MLA pasa a su columna (venta por publicación) y otro texto queda
  // marcado como fuera del catálogo. Desde la columna MLA (`campo` = 'mlaId') sólo se busca si el renglón no tiene SKU, y
  // sin coincidencia queda como está (se vende por publicación; el backend valida al guardar).
  const formRef = useRef(form);
  formRef.current = form;
  const resolverSku = async (key: string, campo: 'skuInterno' | 'mlaId' = 'skuInterno') => {
    const it = formRef.current.items.find(x => x._key === key);
    const texto = it?.[campo].trim() ?? '';
    const yaResuelto = campo === 'mlaId' ? !!it?.skuInterno.trim() : it?.skuInterno === it?.resuelto;
    if (!it || texto.length < MIN_CARACTERES_SKU || yaResuelto) return;
    try {
      const lista = await buscarArticulos(texto);
      const t = texto.toLowerCase();
      const a = lista.find(x => x.skuInterno.toLowerCase() === t || x.mlaId?.toLowerCase() === t) ?? (lista.length === 1 ? lista[0] : undefined);
      const ahora = formRef.current.items.find(x => x._key === key);
      if (ahora?.skuInterno !== it.skuInterno || ahora.mlaId !== it.mlaId) return;
      if (a) aplicarArticulo(key, a);
      else if (campo === 'mlaId') return;
      else if (PARECE_MLA.test(texto) && !it.mlaId.trim()) updateItem(key, { skuInterno: '', mlaId: texto.toUpperCase(), fueraDeCatalogo: 'mla' });
      else updateItem(key, { fueraDeCatalogo: 'sku' });
    } catch { /* sin resolver: el backend valida el SKU al guardar y pide el color si hace falta */ }
  };

  /** Colores a elegir en el ítem (vacío si el SKU no se vende por variantes o manda el BOM). */
  const coloresDe = (it: Linea) => (it.skuInterno ? coloresPorSku[it.skuInterno]?.variantes ?? [] : []);

  const nuevaOV = () => { setEditando(null); setForm(emptyForm()); setItemsError(''); setOpen(true); };
  // Un borrador se abre para editar (si hay permiso); el resto, de sólo lectura.
  const abrirOV = (ov: OrdenVenta) => {
    if (ov.estado !== 'borrador' || !can('ventas', 'editar')) { setDetalle(ov); return; }
    setEditando(ov);
    setForm(formDe(ov));
    setItemsError('');
    setOpen(true);
    new Set((ov.items ?? []).map(it => it.skuInterno).filter((s): s is string => !!s)).forEach(cargarColores);
  };

  const handleMlSync = async () => {
    if (!obligatoriosMl.validar()) return;
    const id = mlOrderId.trim();
    setMlSyncing(true);
    try {
      const r = await api.post<{ mlOrderId: number; ovId: number; estado: string }>(`/api/v3/ml/orders/${id}/sync`, {});
      toast.addToast(`Orden ML #${r.mlOrderId} importada → OV #${r.ovId} (${r.estado})`, 'success');
      setMlSyncOpen(false);
      setMlOrderId('');
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setMlSyncing(false);
    }
  };

  /** Guarda la OV (nueva o el borrador abierto) y, con `confirmar`, la confirma: si no se puede (p. ej. falta stock) queda en borrador. */
  const guardar = async (confirmar: boolean) => {
    setItemsError('');
    if (!obligatorios.validar()) return;
    // Como reemplazarItems: en un borrador sólo se pide el color de los renglones nuevos o cuyo MLA o SKU cambió; el que
    // conserva los de uno guardado (en orden, si hay varios iguales) queda como estaba, aunque no tenga color (p. ej. de ML).
    const clave = (it: { mlaId?: string | null; skuInterno?: string | null }) => `${(it.mlaId ?? '').trim()}|${(it.skuInterno ?? '').trim()}`;
    const guardadas = (editando?.items ?? []).map(clave);
    const sinColor = form.items.find(it => {
      const previa = guardadas.indexOf(clave(it));
      if (previa >= 0) { guardadas.splice(previa, 1); return false; }
      return coloresDe(it).length > 0 && it.varianteId == null;
    });
    if (sinColor) {
      const aviso = `Elegí el color de ${sinColor.skuInterno}`;
      setItemsError(aviso);
      toast.addToast(aviso, 'error');
      return;
    }
    const fuera = form.items.find(it => it.fueraDeCatalogo === 'sku');
    if (fuera) {
      const aviso = `${fuera.skuInterno} no está en el catálogo: elegí un artículo de la lista`;
      setItemsError(aviso);
      toast.addToast(aviso, 'error');
      return;
    }
    setItemsError('');
    setGuardando(confirmar ? 'confirmar' : 'borrador');
    const items = form.items.map(it => ({
      mlaId: it.mlaId.trim(),
      descripcion: it.descripcion,
      skuInterno: it.skuInterno.trim() || undefined,
      varianteId: coloresDe(it).length > 0 ? it.varianteId : undefined,
      cantidad: Number(it.cantidad),
      precioUnitario: Number(it.precioUnitario),
      descuentoPorcentaje: Number(it.descuentoPorcentaje) || 0,
    }));
    let id = editando?.id;
    try {
      if (editando) {
        await api.put(`/api/v3/ventas/${editando.id}/items`, { items });
        await api.patch(`/api/v3/ventas/${editando.id}`, { notas: form.notas, direccionEntrega: form.direccionEntrega });
      } else {
        const creada = await api.post<{ id: number }>('/api/v3/ventas', {
          clienteId: form.clienteId ? Number(form.clienteId) : undefined,
          canal: form.canal,
          fecha: form.fecha,
          numeroOrden: form.numeroOrden.trim() || undefined,
          direccionEntrega: form.direccionEntrega.trim() || undefined,
          notas: form.notas.trim() || undefined,
          items,
        });
        id = creada.id;
      }
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
      setGuardando(false);
      return;
    }
    const eraEdicion = !!editando;
    cerrarForm();
    if (confirmar) {
      try {
        await api.post(`/api/v3/ventas/${id}/confirmar`, {});
        toast.addToast('OV confirmada — stock reservado', 'success');
        avisarImpactoMl(items.map(it => it.skuInterno));
      } catch (e) {
        toast.addToast(`OV #${id} guardada en borrador; no se pudo confirmar: ${extractErrorMessage(e)}`, 'error');
      }
    } else {
      toast.addToast(eraEdicion ? `OV #${id} guardada (borrador)` : 'Orden de venta creada (borrador)', 'success');
    }
    setGuardando(false);
    refetch();
  };

  const doAction = async (ov: OrdenVenta, action: 'confirmar' | 'cancelar') => {
    try {
      await api.post(`/api/v3/ventas/${ov.id}/${action}`, {});
      toast.addToast(action === 'confirmar' ? 'OV confirmada — stock reservado' : 'OV cancelada — reserva liberada', 'success');
      if (action === 'confirmar') avisarImpactoMl((ov.items ?? []).map(it => it.skuInterno));
      setDetalle(null);
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  const puedeEditar = can('ventas', 'editar');
  const claseAccion = 'text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
  const acciones = (r: OrdenVenta) => (
    <>
      {r.estado === 'borrador' && (
        <button onClick={() => doAction(r, 'confirmar')} disabled={!puedeEditar} title={!puedeEditar ? SIN_PERMISO : undefined}
          className={`text-brand-600 hover:text-brand-800 ${claseAccion}`}>Confirmar</button>
      )}
      {(r.estado === 'borrador' || r.estado === 'confirmada') && (
        <button onClick={() => setACancelar(r)} disabled={!puedeEditar} title={!puedeEditar ? SIN_PERMISO : undefined}
          className={`text-red-600 hover:text-red-800 ${claseAccion}`}>Cancelar</button>
      )}
    </>
  );
  const badgeEstado = (estado: string) => <Badge tone={ESTADO_OV[estado]?.tono ?? 'slate'}>{ESTADO_OV[estado]?.etiqueta ?? estado}</Badge>;

  const columns = [
    { key: 'id', header: '#' },
    { key: 'fecha', header: 'Fecha', render: (r: OrdenVenta) => new Date(r.fecha).toLocaleDateString('es-AR', { timeZone: 'UTC' }) },
    { key: 'numeroOrden', header: 'N° de orden', render: (r: OrdenVenta) => r.numeroOrden || <span className="text-slate-400 text-xs">—</span> },
    { key: 'clienteNombre', header: 'Cliente', render: (r: OrdenVenta) => r.clienteNombre ?? 'Mostrador' },
    { key: 'canal', header: 'Canal', render: (r: OrdenVenta) => CANAL[r.canal] ?? r.canal },
    {
      key: 'modalidadEnvio', header: 'Envío', filterable: false,
      render: (r: OrdenVenta) => r.modalidadEnvio
        ? <Badge tone={MODALIDAD_TONO[r.modalidadEnvio] ?? 'slate'}>{r.modalidadEnvio}</Badge>
        : <span className="text-slate-400 text-xs">—</span>,
    },
    { key: 'estado', header: 'Estado', render: (r: OrdenVenta) => badgeEstado(r.estado) },
    { key: 'total', header: 'Total', render: (r: OrdenVenta) => pesos(Number(r.total)) },
    {
      key: 'actions',
      header: '',
      filterable: false,
      render: (r: OrdenVenta) => <div className="flex gap-3 items-center" onClick={e => e.stopPropagation()}>{acciones(r)}</div>,
    },
  ];

  const puedeGuardar = editando ? puedeEditar : can('ventas', 'crear');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Órdenes de Venta</h1>
          <HelpTip tips={['Cada ítem lleva el SKU (buscalo por SKU, descripción o MLA) o el número de publicación (MLA ID)', 'En las ventas de MercadoLibre el MLA ID es obligatorio en cada ítem', 'Si el SKU tiene variantes (colores), elegí el color de cada ítem: es obligatorio', 'Guardar y confirmar reserva el stock disponible (ATP); un borrador se puede abrir y editar', 'Cancelar libera la reserva', 'Independiente del picking físico del WMS']} />
          <p className="text-slate-500 text-sm mt-1">Ventas — mostrador y MercadoLibre</p>
        </div>
        <div className="flex items-center gap-3">
          <MenuImportar opciones={[
            { etiqueta: 'Orden de MercadoLibre', descripcion: 'Trae una orden de ML por su número', onClick: () => setMlSyncOpen(true), deshabilitado: !puedeEditar },
          ]} />
          <Button onClick={nuevaOV} disabled={!can('ventas', 'crear')} title={!can('ventas', 'crear') ? SIN_PERMISO : undefined}>
            + Nueva OV
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label htmlFor="ov-canal" className="block text-xs font-medium text-slate-600 mb-1">Canal</label>
          <select id="ov-canal" className={claseFiltro} value={filtros.canal} onChange={e => filtrar('canal', e.target.value)}>
            <option value="">Todos</option>
            <option value="mostrador">Mostrador</option>
            <option value="mercadolibre">MercadoLibre</option>
          </select>
        </div>
        <div>
          <label htmlFor="ov-estado" className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
          <select id="ov-estado" className={claseFiltro} value={filtros.estado} onChange={e => filtrar('estado', e.target.value)}>
            <option value="">Todos</option>
            {['borrador', 'confirmada', 'entregada', 'facturada', 'cancelada'].map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ov-desde" className="block text-xs font-medium text-slate-600 mb-1">Desde</label>
          <input id="ov-desde" type="date" className={claseFiltro} value={filtros.fecha_desde} onChange={e => filtrar('fecha_desde', e.target.value)} />
        </div>
        <div>
          <label htmlFor="ov-hasta" className="block text-xs font-medium text-slate-600 mb-1">Hasta</label>
          <input id="ov-hasta" type="date" className={claseFiltro} value={filtros.fecha_hasta} onChange={e => filtrar('fecha_hasta', e.target.value)} />
        </div>
        <div>
          <label htmlFor="ov-buscar" className="block text-xs font-medium text-slate-600 mb-1">Buscar</label>
          <input id="ov-buscar" className={claseFiltro} placeholder="N° de orden o cliente" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        onRowClick={abrirOV}
        emptyMessage="No hay órdenes de venta para los filtros"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={totalOrdenes}
        onChange={setPage}
        onPageSizeChange={s => { setPageSize(s); setPage(1); }}
      />
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title={`OV #${detalle?.id ?? ''}`} size="xl">
        {detalle && (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><dt className="text-xs text-slate-500">Cliente</dt><dd className="font-medium">{detalle.clienteNombre ?? 'Mostrador'}</dd></div>
              <div><dt className="text-xs text-slate-500">Canal</dt><dd>{CANAL[detalle.canal] ?? detalle.canal}{detalle.modalidadEnvio ? ` · ${detalle.modalidadEnvio}` : ''}</dd></div>
              <div><dt className="text-xs text-slate-500">Fecha</dt><dd>{new Date(detalle.fecha).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</dd></div>
              <div><dt className="text-xs text-slate-500">Estado</dt><dd>{badgeEstado(detalle.estado)}</dd></div>
              <div><dt className="text-xs text-slate-500">N° de orden</dt><dd>{detalle.numeroOrden || '—'}</dd></div>
              <div className="md:col-span-2"><dt className="text-xs text-slate-500">Dirección de entrega</dt><dd>{detalle.direccionEntrega || '—'}</dd></div>
              <div className="col-span-2 md:col-span-4"><dt className="text-xs text-slate-500">Notas</dt><dd className="whitespace-pre-line">{detalle.notas || '—'}</dd></div>
            </dl>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 border-b"><th className="pb-1">SKU</th><th className="pb-1">MLA ID</th><th className="pb-1">Descripción</th><th className="pb-1">Color</th><th className="pb-1 text-right">Cant.</th><th className="pb-1 text-right">Precio</th><th className="pb-1 text-right">Desc. %</th><th className="pb-1 text-right">Subtotal</th></tr></thead>
                <tbody>
                  {(detalle.items ?? []).map((it, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1.5 font-mono text-xs">{it.skuInterno || '—'}</td>
                      <td className="py-1.5 text-xs text-slate-500">{it.mlaId || '—'}</td>
                      <td className="py-1.5">{it.descripcion}</td>
                      <td className="py-1.5 text-xs text-slate-500">{it.variante?.nombre ?? '—'}</td>
                      <td className="py-1.5 text-right">{Number(it.cantidad)}</td>
                      <td className="py-1.5 text-right">{pesos(Number(it.precioUnitario))}</td>
                      <td className="py-1.5 text-right">{Number(it.descuentoPorcentaje ?? 0) ? `${Number(it.descuentoPorcentaje)}%` : '—'}</td>
                      <td className="py-1.5 text-right font-medium">{pesos(Number(it.subtotal ?? brutoLinea(it)))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={7} className="pt-2 text-right font-semibold text-slate-600">Total:</td><td className="pt-2 text-right font-bold">{pesos(Number(detalle.total))}</td></tr></tfoot>
              </table>
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t">
              <Button variant="ghost" onClick={() => setDetalle(null)}>Cerrar</Button>
              {(detalle.estado === 'borrador' || detalle.estado === 'confirmada') && (
                <Button variant="danger" onClick={() => setACancelar(detalle)} disabled={!puedeEditar} title={!puedeEditar ? SIN_PERMISO : undefined}>Cancelar OV</Button>
              )}
              {detalle.estado === 'borrador' && (
                <Button onClick={() => doAction(detalle, 'confirmar')} disabled={!puedeEditar} title={!puedeEditar ? SIN_PERMISO : undefined}>Confirmar</Button>
              )}
            </div>
          </div>
        )}
      </Modal>
      <Confirm
        open={!!aCancelar}
        title={`Cancelar OV #${aCancelar?.id ?? ''}`}
        message="La OV pasa a cancelada y se libera el stock que tenía reservado."
        confirmLabel="Cancelar OV"
        danger
        onCancel={() => setACancelar(null)}
        onConfirm={() => { const ov = aCancelar!; setACancelar(null); void doAction(ov, 'cancelar'); }}
      />
      <Modal open={mlSyncOpen} onClose={() => { setMlSyncOpen(false); setMlOrderId(''); }} title="Importar orden de MercadoLibre" size="sm">
        <div {...obligatoriosMl.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <p className="text-sm text-slate-600">Ingresá el ID numérico de la orden de ML para traerla al sistema. Lo encontrás en la URL de la orden o en tu panel de ventas de ML.</p>
          <div>
            <label className="block text-sm font-medium mb-1">ID de orden ML<Obligatorio /></label>
            <input
              type="number"
              aria-label="ID de orden ML"
              required
              placeholder="Ej: 2000007890123456"
              value={mlOrderId}
              onChange={e => setMlOrderId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleMlSync()}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => { setMlSyncOpen(false); setMlOrderId(''); }} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={handleMlSync} loading={mlSyncing}>{mlSyncing ? 'Importando...' : 'Importar'}</Button>
          </div>
        </div>
      </Modal>
      <Modal open={open} cierre={cierreForm} title={editando ? `OV #${editando.id} — borrador` : 'Nueva Orden de Venta'} size="full">
        <div {...obligatorios.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium mb-1">Cliente</label>
              <input aria-label="Cliente" value={form.clienteTexto} placeholder="Mostrador (sin cliente)" autoComplete="off" disabled={!!editando}
                onChange={e => { setForm({ ...form, clienteTexto: e.target.value, clienteId: '' }); setVerClientes(true); }}
                onFocus={() => setVerClientes(true)} onBlur={() => setVerClientes(false)}
                className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-50" />
              {verClientes && clientesFiltrados.length > 0 && (
                <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {clientesFiltrados.map(c => (
                    <div key={c.id} onMouseDown={e => { e.preventDefault(); setForm(f => ({ ...f, clienteId: String(c.id), clienteTexto: c.nombre })); setVerClientes(false); }}
                      className="px-3 py-1.5 text-sm hover:bg-brand-50 cursor-pointer">{c.nombre}</div>
                  ))}
                </div>
              )}
              {form.clienteTexto.trim() && !form.clienteId && !editando && <p className="text-xs text-amber-700 mt-1">Elegí el cliente de la lista; si no, la venta queda como mostrador.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Canal</label>
              <select aria-label="Canal" value={form.canal} disabled={!!editando} onChange={e => setForm({ ...form, canal: e.target.value })} className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-50">
                <option value="mostrador">Mostrador</option>
                <option value={ML}>MercadoLibre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha<Obligatorio /></label>
              <input type="date" aria-label="Fecha" required disabled={!!editando} value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">N° de orden</label>
              <input aria-label="N° de orden" value={form.numeroOrden} disabled={!!editando} onChange={e => setForm({ ...form, numeroOrden: e.target.value })} className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Dirección de entrega</label>
              <input aria-label="Dirección de entrega" value={form.direccionEntrega} onChange={e => setForm({ ...form, direccionEntrega: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notas</label>
              <input aria-label="Notas" value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>

          <h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Items</h3>
          <div style={ESTILO_COLUMNAS}>
            <div className={`hidden md:grid ${COLUMNAS_LINEA} md:gap-1 text-[10px] text-slate-400 px-1 py-1 border-b border-slate-100`}>
              <span>SKU{!esMl && <Obligatorio />}</span><span>MLA ID{esMl && <Obligatorio />}</span><span>Descripción<Obligatorio /></span>
              <span className="text-right">Cant.<Obligatorio /></span><span className="text-right">Precio<Obligatorio /></span><span className="text-right">Desc. %</span><span className="text-right">Subtotal</span><span></span>
            </div>
            <div className="space-y-2 pt-1">
              {form.items.map((it, idx) => (
                // Un solo dibujo por renglón: tarjeta en el celular, fila de columnas desde md.
                <div key={it._key} data-fila={it._key} className="rounded-lg border border-slate-200 bg-white p-3 md:border-0 md:bg-transparent md:p-0 md:rounded-none">
                  <div className={`grid grid-cols-2 gap-2 md:gap-1 ${COLUMNAS_LINEA} md:items-start`}>
                    <div className="col-span-2 md:col-span-1"
                      // Enter sin elegir de la lista: pasa a la cantidad (al salir del buscador se resuelve lo escrito).
                      onKeyDown={e => { if (e.key === 'Enter' && !e.defaultPrevented) { e.preventDefault(); setFoco({ key: it._key, campo: 'cant' }); } }}>
                      <span className={ETIQUETA}>SKU{!esMl && <Obligatorio />}</span>
                      <BuscadorSku
                        value={it.skuInterno}
                        onChange={texto => updateItem(it._key, { skuInterno: texto, varianteId: null, fueraDeCatalogo: undefined })}
                        onElegir={s => elegirArticulo(it._key, s as Articulo)}
                        onBlur={() => void resolverSku(it._key)}
                        buscar={buscarArticulos}
                        placeholder="SKU, descripción o MLA"
                        title="Buscá el artículo por SKU, descripción o MLA (flechas y Enter para elegir)"
                        data-buscar=""
                        data-campo="SKU o MLA ID"
                        {...faltaSi(!it.skuInterno.trim() && !it.mlaId.trim(), !esMl)}
                        className={CAMPO}
                      />
                      {it.fueraDeCatalogo === 'sku' && <p role="alert" className="mt-0.5 text-xs text-red-600">No está en el catálogo: elegí un artículo de la lista.</p>}
                      {it.fueraDeCatalogo === 'mla' && <p className="mt-0.5 text-xs text-amber-700">Fuera del catálogo: se vende por publicación, sin reservar stock.</p>}
                    </div>
                    <div>
                      <span className={ETIQUETA}>MLA ID{esMl && <Obligatorio />}</span>
                      <input placeholder="MLA ID" data-campo="MLA ID" {...faltaSi(!it.mlaId.trim(), esMl)} value={it.mlaId} autoComplete="off"
                        onChange={e => updateItem(it._key, { mlaId: e.target.value, ...(it.fueraDeCatalogo === 'mla' ? { fueraDeCatalogo: undefined } : {}) })}
                        onBlur={() => void resolverSku(it._key, 'mlaId')} onKeyDown={e => { if (e.key === 'Enter') void resolverSku(it._key, 'mlaId'); }} className={CAMPO}
                        title={esMl ? 'Número de publicación de MercadoLibre (obligatorio en ventas de MercadoLibre)' : 'Número de publicación de MercadoLibre (opcional)'} />
                    </div>
                    <div className="md:col-span-1">
                      <span className={ETIQUETA}>Descripción<Obligatorio /></span>
                      <input placeholder="Descripción" data-campo="Descripción" required {...faltaSi(!it.descripcion.trim())} value={it.descripcion} onChange={e => updateItem(it._key, { descripcion: e.target.value })} className={CAMPO} />
                    </div>
                    <div>
                      <span className={ETIQUETA}>Cant.<Obligatorio /></span>
                      <input type="number" placeholder="Cant." data-campo="Cantidad" data-cant="" required min="0.01" step="any" value={it.cantidad}
                        onChange={e => updateItem(it._key, { cantidad: Number(e.target.value) })} onKeyDown={alSiguienteRenglon(idx)} className={`${CAMPO} text-right`} />
                    </div>
                    <div>
                      <span className={ETIQUETA}>Precio<Obligatorio /></span>
                      <input type="number" placeholder="Precio" data-campo="Precio" required min="0" step="any" value={it.precioUnitario}
                        onChange={e => updateItem(it._key, { precioUnitario: Number(e.target.value) })} onKeyDown={alSiguienteRenglon(idx)} className={`${CAMPO} text-right`} />
                    </div>
                    <div>
                      <span className={ETIQUETA}>Desc. %</span>
                      <input type="number" placeholder="Desc. %" aria-label="Descuento (%)" min="0" max="100" step="any" value={it.descuentoPorcentaje || ''}
                        onChange={e => updateItem(it._key, { descuentoPorcentaje: Number(e.target.value) })} onKeyDown={alSiguienteRenglon(idx)} className={`${CAMPO} text-right`} />
                    </div>
                    <div>
                      <span className={ETIQUETA}>Subtotal</span>
                      <div className="px-2 py-1.5 bg-slate-50 rounded text-sm text-right font-medium">{pesos(subtotalLinea(it))}</div>
                    </div>
                    <button type="button" onClick={() => quitarLinea(it._key)} className="justify-self-end self-center text-red-500 min-w-[2rem] min-h-[2rem]" title="Quitar ítem" aria-label="Quitar ítem">✕</button>
                  </div>
                  {it.skuInterno && coloresPorSku[it.skuInterno]?.variosFisicos && (
                    <p className="mt-2 text-xs text-amber-700">Este combo no descuenta colores; cargá su detalle por color en 02_CODIGOS (varN).</p>
                  )}
                  {coloresDe(it).length > 0 && (
                    <div className="mt-2">
                      <span className="block text-xs font-medium text-slate-500 mb-1">Color de {it.skuInterno}<Obligatorio /></span>
                      <ChipsVariante etiqueta="Color" variantes={coloresDe(it)} valor={it.varianteId ?? null}
                        onChange={id => updateItem(it._key, { varianteId: id })} conSinAsignar={false} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <button type="button" onClick={agregarLinea} className="self-start shrink-0 inline-flex items-center px-3 py-1.5 min-h-[2.5rem] rounded-lg border border-dashed border-brand-300 text-brand-700 text-sm font-medium hover:bg-brand-50">+ Agregar ítem</button>
            <p className="text-xs text-slate-500">Con el teclado: elegí el artículo con las flechas y Enter, cargá la cantidad y Enter pasa al ítem siguiente.</p>
          </div>
          {itemsError && <p className="text-red-600 text-sm">{itemsError}</p>}
          <div className="sticky bottom-0 -mx-6 px-6 bg-white pt-3 pb-1 border-t border-slate-200 flex flex-wrap items-center justify-end gap-3">
            <p className="mr-auto text-sm text-slate-600">
              {bruto - total > 0.005 && <>Subtotal: {pesos(bruto)} · Descuento: −{pesos(bruto - total)} · </>}
              <span className="font-semibold text-slate-800">Total: {pesos(total)}</span>
            </p>
            <button onClick={cierreForm.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button variant="ghost" onClick={() => guardar(false)} loading={guardando === 'borrador'} disabled={!!guardando || !puedeGuardar} title={!puedeGuardar ? SIN_PERMISO : undefined}>
              Guardar borrador
            </Button>
            <Button onClick={() => guardar(true)} loading={guardando === 'confirmar'} disabled={!!guardando || !puedeGuardar || !puedeEditar} title={!puedeGuardar || !puedeEditar ? SIN_PERMISO : undefined}>
              Guardar y confirmar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
